import { useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
import {
  isBaseballSummary,
  isTennisSummary,
  type BaseballSummary,
  type MatchSummary,
  type TennisSummary,
} from '../utils/matchEventsFeed';
// Two distinct cues so a primary vs secondary incident is tellable by ear.
import goalSound from 'src/Sounds/goal.mp3';
import cornerSound from 'src/Sounds/UI_Quirky7.mp3';

/** Combined total (home + away) of a `{ home, away }` pair, or null. */
function pairTotal(pair: { home: number; away: number } | null): number | null {
  if (!pair) return null;
  const sum = pair.home + pair.away;
  return Number.isFinite(sum) ? sum : null;
}

/**
 * The two metrics that trigger a cue, per sport:
 *   - soccer   → primary = goals, secondary = corners
 *   - baseball → primary = runs,  secondary = home runs
 *   - tennis   → primary = game score, secondary = none
 */
function soundMetrics(
  summary: MatchSummary | BaseballSummary | TennisSummary,
): { primary: number | null; secondary: number | null } {
  if (isBaseballSummary(summary)) {
    return {
      primary: pairTotal(summary.runs),
      secondary: pairTotal(summary.homeRuns),
    };
  }
  if (isTennisSummary(summary)) {
    return {
      primary: summary.game
        ? pairTotal({
            home: Number(summary.game.home) || 0,
            away: Number(summary.game.away) || 0,
          })
        : null,
      secondary: null,
    };
  }
  return {
    primary: summary.goals ? pairTotal(summary.goals.total) : null,
    secondary: summary.corners ? pairTotal(summary.corners.total) : null,
  };
}

/**
 * Plays a cue when the sport's primary or secondary tally INCREASES — a real
 * new goal/run or corner/home-run, not a confidence re-emit (the summary
 * already collapses to the authoritative reading). The two metrics get separate
 * audio players + clips so a simultaneous pair doesn't clobber. The first
 * observed value only seeds the baseline (no cue on tab open), and a decrease
 * (VAR revert) is silent.
 *
 * Baselines reset when `fixtureId` changes so switching matches never fires a
 * spurious cue off the new match's opening totals.
 */
export function useMatchEventSound(
  summary: MatchSummary | BaseballSummary | TennisSummary,
  opts: { enabled: boolean; fixtureId: number | null; ready?: boolean },
): void {
  const { load: loadPrimary } = useAudioPlayer();
  const { load: loadSecondary } = useAudioPlayer();

  const prevPrimary = useRef<number | null>(null);
  const prevSecondary = useRef<number | null>(null);
  const prevFixture = useRef<number | null>(null);

  useEffect(() => {
    // New fixture → drop baselines so the first reading only seeds them.
    if (opts.fixtureId !== prevFixture.current) {
      prevFixture.current = opts.fixtureId;
      prevPrimary.current = null;
      prevSecondary.current = null;
    }

    // Not ready (history still loading) → don't touch baselines or play, so the
    // history load bumping the total from 0 to N never fires a spurious cue.
    // The FIRST summary once ready only seeds the baselines.
    if (opts.ready === false) {
      prevPrimary.current = null;
      prevSecondary.current = null;
      return;
    }

    const { primary, secondary } = soundMetrics(summary);

    if (primary != null) {
      if (
        opts.enabled &&
        prevPrimary.current != null &&
        primary > prevPrimary.current
      ) {
        try {
          loadPrimary(goalSound, { autoplay: true, loop: false });
        } catch {
          /* audio unavailable — the row still renders */
        }
      }
      prevPrimary.current = primary;
    }

    if (secondary != null) {
      if (
        opts.enabled &&
        prevSecondary.current != null &&
        secondary > prevSecondary.current
      ) {
        try {
          loadSecondary(cornerSound, { autoplay: true, loop: false });
        } catch {
          /* audio unavailable */
        }
      }
      prevSecondary.current = secondary;
    }
  }, [
    summary,
    opts.enabled,
    opts.fixtureId,
    opts.ready,
    loadPrimary,
    loadSecondary,
  ]);
}
